/**
 * RBAC-8.1 · Read a role's history, preview a restore, restore.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES AND READS NO TABLE. `role_permission_versions` has no API grant at all;
 * who may read it, what a version is and when a restore is refused are `20261103a2`. What the answers
 * mean is `lib/roleHistory.ts`. Errors carry the server's sentence verbatim, as RBAC-3.6's do.
 */

import { supabase } from '@/lib/supabaseClient';
import { parseRoleSaveDiff, type RoleSaveDiff } from '@/lib/rolePermissionDiff';
import {
  ROLE_PERMISSION_HISTORY_RPC,
  ROLE_RESTORE_PREVIEW_RPC,
  ROLE_RESTORE_VERSION_RPC,
  parseRestorePreview,
  parseRoleHistory,
  restoreRoleArgs,
  type RestorePreview,
  type RoleHistory,
} from '@/lib/roleHistory';
import { RoleSaveError } from '@/services/roleSaveService';

const unreadable = (what: string) =>
  new RoleSaveError({ message: `${what} came back in a shape this screen does not understand. Reload to see the current state.` });

export async function fetchRoleHistory(roleId: number): Promise<RoleHistory> {
  const { data, error } = await supabase.rpc(ROLE_PERMISSION_HISTORY_RPC, { p_role_id: roleId });
  if (error) throw new RoleSaveError(error);
  const h = parseRoleHistory(data);
  if (!h) throw unreadable("This role's history");
  return h;
}

export async function previewRoleRestore(roleId: number, versionId: number): Promise<RestorePreview> {
  const { data, error } = await supabase.rpc(ROLE_RESTORE_PREVIEW_RPC, { p_role_id: roleId, p_version_id: versionId });
  if (error) throw new RoleSaveError(error);
  const p = parseRestorePreview(data);
  if (!p) throw unreadable('The list of changes');
  return p;
}

/** `reviewed` must be the preview the admin confirmed: its count is the number on the button. */
export async function restoreRoleVersion(
  roleId: number,
  versionId: number,
  base: readonly string[],
  reviewed: RoleSaveDiff,
): Promise<RoleSaveDiff> {
  const { data, error } = await supabase.rpc(ROLE_RESTORE_VERSION_RPC, restoreRoleArgs(roleId, versionId, base, reviewed));
  if (error) throw new RoleSaveError(error);
  const d = parseRoleSaveDiff(data);
  if (!d) throw unreadable('The restored role');
  return d;
}
